import React, { useMemo, useRef, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { format, parse, isValid } from 'date-fns';
import {
	Box,
	Card,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Button,
	Grid,
	Stack,
	TextField,
	Typography,
	MenuItem,
	ButtonBase,
	Chip,
	Switch,
} from "@mui/material";
import { useAuth } from '../../../contexts/AuthContext';
import { usePost } from '../../../apis/ApiRequests';
import CustomCheckbox from '../../../components/input/CustomCheckbox';
import CustomConfirm from '../../../components/confirm/CustomConfirm';
import CustomSelect from '../../../components/select/CustomSelect';
import CustomSnackBar from '../../../components/alert/CustomSnackBar';
import Header from '../../../components/uiParts/port/Header';
import TitleBar from '../../../components/uiParts/TitleBar';
import Frame from "../../../components/uiParts/Frame";
import { dateToString } from '../../../utils/Library';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { facilityData } from "../../../Store";

const DOCKS = [1, 2, 3, 4, 5];

const outTimeStr = "時間外";

// 表示パラメータ
const START_HOURS = 3;
const END_HOURS = 16;
const OPERATING_HOURS = END_HOURS - START_HOURS;
const MINUTES_STEP = 15;
const START_MINUTES = START_HOURS * 60;
const END_MINUTES = END_HOURS * 60;
const OPERATING_MINUTES = OPERATING_HOURS * 60;

// 1分あたりの高さ(px) → 15分で 30px
const PX_PER_MIN = 2;
const HEADER_H = 44;
const TIME_COL_W = 70;

function pad2(n) {
  return String(n).padStart(2, "0");
}
//分⇒HH:MM
function minutesToHHMM(m) {
  const hh = Math.floor(m / 60);
  const mm = m % 60;
  return `${pad2(hh)}:${pad2(mm)}`;
}
//HH:MM⇒分
function hhmmToMinutes(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}
function nowMinutes() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}
//HHMM⇒HH:MM
function formatHHMM(date,hhmm) {
  const referenceDate = parse(`${date}${hhmm}`,"yyyyMMddHHmm",new Date());
  return format(referenceDate,"HH:mm")
}
//HH:MM⇒HHMM
function unformatHHMM(hhmm) {
  return hhmm.replace(":","");
}
function todayToYYYYMMDD() {
  return format(new Date(),'yyyyMMdd')
}

const PortSchedule = () => {

	const { authInfo, setAuthInfo } = useAuth();

    //===============================================
    // initialize
    //===============================================
    //-----------------------------------------------
    // initialize - セッション情報を取得
    //-----------------------------------------------
    const facilityCode   = authInfo.facilityCode ? authInfo.facilityCode : facilityData.facilityCode;
    const facilityName   = authInfo.facilityName;
    const userId         = authInfo.userId ? authInfo.userId : 'Signage';
	const userName = authInfo.userName;
	const authority = authInfo.authority;
	const traderCode = authInfo.traderCode;
	const trader = authInfo.trader;
    const senmeiCode =
        authInfo.senmeiCode === null
            ? null
            : !isNaN(Number(authInfo.senmeiCode))
                ? Number(authInfo.senmeiCode)
                : authInfo.senmeiCode;
    const senmei = authInfo.senmei;

	const scrollRef = useRef(null);

	const [searchParams] = useSearchParams();
    const systemMode     = Number(searchParams.get("systemMode") ?? 0);

	//-----------------------------------------------
    // initialize - useState
	//-----------------------------------------------
	// 登録日
	const [entryDate, setEntryDate] = useState(todayToYYYYMMDD());

	// 追加/編集ダイアログ
	const [open, setOpen] = useState(false);
	const [editingId, setEditingId] = useState("new");
	const [keepNewId, setKeepNewId] = useState(false);

	// 削除確認
	const [ openConfirm, setOpenConfirm] = useState(false);

	// スナックバー
	const [ openSnackBarTrue,  setOpenSnackBarTrue  ] = useState(false);
	const [ openSnackBarFalse,  setOpenSnackBarFalse  ] = useState(false);
	const [ errorMessage, setErrorMessage ] = useState("");

	// フォーム
	const [form, setForm] = useState({
		dock: 1,
		start: 480,
		end: 540,
		senmeiCode: senmeiCode,
		method: [],
		biko: "",
		outTime: false,
	});

	// 回数でソートされたスケジュール
	const [sortedSchedule, setSortedSchedule] = useState([]);

	// 入船回数の最大
	const maxKaisu = useMemo(() => {
		if (!Array.isArray(sortedSchedule) || sortedSchedule.length === 0) {
			return 0;
		}
		return Math.max(...sortedSchedule.map(item => item.kaisu ?? 0));
	}, [sortedSchedule]);

	// 現在時刻ライン表示用
	const nowMin = nowMinutes();
	const nowY = nowMin * PX_PER_MIN;

	// 1日の高さ
	const bodyH = OPERATING_MINUTES * PX_PER_MIN;

	// 時刻選択用 予約枠
	const timeSlot = useMemo(() => {
		const rows = 24;
		const columns = 60 / MINUTES_STEP;
		return Array.from({length: rows * columns}, (_, index) => ({
			minutes: index * MINUTES_STEP,
			status: '○',
			selected: false
		}));
	}, []);
	const [slotState, setSlotState] = useState(timeSlot);

	//ボタン配置用 グルーピング
	const groupedByHour = useMemo(() => {
		if (OPERATING_HOURS <= 0) return [];

		const result = Array.from({ length: 24 }, () => []);
		slotState.forEach(slot => {
			const hour = Math.floor(slot.minutes / 60);
			if (hour >= START_HOURS && hour < END_HOURS) {
				result[hour - START_HOURS].push(slot);
			}
		});
		return result.filter(hourRow => hourRow.length > 0);
	}, [slotState]);

	//時刻選択用
	const [selectTimeOpen, setSelectTimeOpen] = useState(false);
	const [startIndex, setStartIndex] = useState(null);
	const [endIndex, setEndIndex] = useState(null);
	const [clickPrevIndex, setClickPrevIndex] = useState(null);

    //===============================================
    // API
    //===============================================
	//-----------------------------------------------
    // API - searchDataSenmeiCodeAndDate / PortSchedule
	//-----------------------------------------------
    const { response : searchDataSenmeiCodeAndDate_Res, post : searchDataSenmeiCodeAndDate_Post } = usePost()
    const searchDataSenmeiCodeAndDate = async (scheduleDate,senmeiCode) => {
        const postData = {
            'className'    : 'mch.models.table.PortSchedule',
            'methodName'   : 'searchDataSenmeiCodeAndDate',
            'facilityCode' : facilityCode,
            'userId'       : userId,
			'scheduleDate' : scheduleDate,
			'senmeiCode'   : senmeiCode,
        }
        await searchDataSenmeiCodeAndDate_Post(postData);
    };
    //-----------------------------------------------
    // API - getNextId / PortSchedule
    //-----------------------------------------------
    const { response : getNextId_Res, post : getNextIdPost } = usePost();
    const getNextId_func = async () => {
        const postData = {
            'className'    : 'mch.models.table.PortSchedule',
            'methodName'   : 'getNextId',
            'facilityCode' : facilityCode,
        }
        await getNextIdPost(postData);
    }
	//-----------------------------------------------
    // API - getDataMethodSenmeiList / table.PortSchedule
	//-----------------------------------------------
    const { response : getDataMethodSenmeiList_res, post : getDataMethodSenmeiList_post } = usePost();
    const getDataMethodSenmeiList = async () => {
        const postData = {
            'className'    : 'mch.models.table.PortSchedule',
            'methodName'   : 'getDataMethodSenmeiList',
            'facilityCode' : facilityCode,
            'userId'       : userId,
			'targetDate'   : entryDate
        }
        await getDataMethodSenmeiList_post(postData);
    };
	//-----------------------------------------------
    // API - searchDataByCalendar / table.PortSchedule
	//-----------------------------------------------
    const { response : searchDataByCalendar_res, post : searchDataByCalendar_post } = usePost();
    const searchDataByCalendar = async () => {
        const postData = {
            'className'    : 'mch.models.table.PortSchedule',
            'methodName'   : 'searchDataByCalendar',
            'facilityCode' : facilityCode,
            'userId'       : userId,
			'keyword1'     : entryDate
        }
        await searchDataByCalendar_post(postData);
    };
	//-----------------------------------------------
    // API - setData / table.PortSchedule
	//-----------------------------------------------
    const { response : setData_res, post : setData_post } = usePost();
    const setData = async (registerData) => {
        const postData = {
            'className'       : 'mch.models.table.PortSchedule',
            'methodName'      : 'setData',
            'facilityCode'    : facilityCode,
            'userId'          : userId,
            'scheduleId'      : registerData.scheduleId,
            'scheduleDate'    : registerData.scheduleDate,
            'activeFlag'      : registerData.activeFlag,
			'senmeiCode'      : registerData.senmeiCode,
			'senmei'          : registerData.senmei,
			'methodCodeList'  : registerData.methodCodeList,
			'methodList'      : registerData.methodList,
			'lineNo'          : registerData.lineNo,
			'startMin'        : registerData.startMin,
			'endMin'          : registerData.endMin,
			'biko'            : registerData.biko,
			'kaisu'           : registerData.kaisu
        }
        await setData_post(postData);
    };
	//-----------------------------------------------
    // API - deleteData / table.PortSchedule
	//-----------------------------------------------
    const { response : deleteData_res, post : deleteData_post } = usePost();
    const deleteData = async (scheduleId) => {
        const postData = {
            'className'       : 'mch.models.table.PortSchedule',
            'methodName'      : 'deleteData',
            'facilityCode'    : facilityCode,
            'userId'          : userId,
            'scheduleId'      : scheduleId,
        }
        await deleteData_post(postData);
    };
	//-----------------------------------------------
	// API - searchData / masta.Ship
	//-----------------------------------------------
	const { response : getDataAll_ship_res, post : getDataAll_ship_post } = usePost();
	const getDataAll_ship = async () => {
		const postData = {
			'className'    : 'mch.models.masta.Ship',
			'methodName'   : 'searchData',
			'facilityCode' : facilityCode,
			'userId'       : userId
		}
		await getDataAll_ship_post(postData);
	};
	//-----------------------------------------------
	// API - searchData / masta.FishMethod
	//-----------------------------------------------
	const { response : getDataAll_fishMethod_res, post : getDataAll_fishMethod_post } = usePost();
	const getDataAll_fishMethod = async () => {
		const postData = {
			'className'    : 'mch.models.masta.FishMethod',
			'methodName'   : 'searchData',
			'facilityCode' : facilityCode,
			'userId'       : userId
		}
		await getDataAll_fishMethod_post(postData);
	};
    //===============================================
    // useEffect
    //===============================================
    //-----------------------------------------------
    // useEffect - ロード時の設定
    //-----------------------------------------------
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;

		const scrollToNow = () => {
			const m = nowMinutes();
			const targetY = (m - START_MINUTES) * PX_PER_MIN;
			const offset = HEADER_H + 80;
			const maxScroll = el.scrollHeight - el.clientHeight;
			const nextTop = Math.max(0, Math.min(targetY - offset, maxScroll));
			el.scrollTop = nextTop;
		};

		// 初回
		fetchData();
		const t = setTimeout(scrollToNow, 0);

		if (systemMode !== 1) {
			return () => clearTimeout(t);
		}

        document.body.classList.add("port");
		const interval = setInterval(() => {
			fetchData();
			setTimeout(scrollToNow, 0);
		}, 10000);

		return () => {
			clearTimeout(t);
			clearInterval(interval);
		};
	}, [systemMode]);
	//-----------------------------------------------
    // useEffect - 登録日
    //-----------------------------------------------
	useEffect(()=>{
		getDataMethodSenmeiList();
		searchDataByCalendar();
	},[entryDate])
	//-----------------------------------------------
    // useEffect - APIレスポンス(searchDataSenmeiCodeAndDate / table.PortSchedule)
    //-----------------------------------------------
	useEffect(()=>{
		if(searchDataSenmeiCodeAndDate_Res){
			const sortSchedule = [...(searchDataSenmeiCodeAndDate_Res?.Data ?? [])].sort(
					(a, b) => (a.kaisu ?? 0) - (b.kaisu ?? 0)
			)
			setSortedSchedule(sortSchedule)
			setKeepNewId(false);
			if(editingId==="new"){
				setForm(prev =>{
					const next = {
						...prev, 
						method: [], 
						biko: "",
						kaisu:maxKaisu + 1 ,
					}
					return next;
				});
				return;
			}
			const selectedSchedule = (sortSchedule ?? []).find((item) => item.scheduleId === editingId)
			if(selectedSchedule){
				setForm(prev =>{
					const next = {
						dock: selectedSchedule.lineNo || prev.dock,
						start: selectedSchedule.startMin && !isNaN(selectedSchedule.startMin) ? hhmmToMinutes(formatHHMM(selectedSchedule.scheduleDate,selectedSchedule.startMin)) : prev.start,
						end: selectedSchedule.endMin && !isNaN(selectedSchedule.endMin) ? hhmmToMinutes(formatHHMM(selectedSchedule.scheduleDate,selectedSchedule.endMin)) : prev.end,
						senmeiCode: selectedSchedule.senmeiCode || prev.senmeiCode,
						biko: selectedSchedule.biko || "",
						method: selectedSchedule.methodCodeList || [],
						kaisu: selectedSchedule.kaisu || 1,
						outTime: selectedSchedule.startMin === outTimeStr || selectedSchedule.endMin === outTimeStr
					}
					return next;
				});	
			}else{
				setForm((p) => ({...p, method: [], biko: "", kaisu: maxKaisu + 1, outTime:false }));
			}
		}
	},[searchDataSenmeiCodeAndDate_Res])
	//-----------------------------------------------
    // useEffect - APIレスポンス(getNextId / table.PortSchedule)
    //-----------------------------------------------
	useEffect(()=>{
		if(getNextId_Res){
            buildPayloadAndSetData(getNextId_Res.scheduleId,maxKaisu + 1);
		}
	},[getNextId_Res])
	//-----------------------------------------------
    // useEffect - APIレスポンス(setData / table.PortSchedule)
    //-----------------------------------------------
	useEffect(()=>{
		if(setData_res){
			fetchData();
			if(setData_res.statusCode){
				handleClickSnackBarTrue();
				setOpen(false);
            }else{
                setErrorMessage(setData_res?.result);
				handleClickSnackBarFalse();
			}
		}
	},[setData_res])
	//-----------------------------------------------
    // useEffect - APIレスポンス(deleteData / table.PortSchedule)
    //-----------------------------------------------
	useEffect(()=>{
		fetchData();
		if(deleteData_res){
			if(deleteData_res.statusCode){
				handleClickSnackBarTrue();
				setOpen(false);
			}else {
				setErrorMessage("削除に失敗しました");
				handleClickSnackBarFalse();
			}
		}
	},[deleteData_res])
    //===============================================
    // eventHandler
    //===============================================
    //-----------------------------------------------
    // eventHandler - 日付の変更
    //-----------------------------------------------
	const handleChange_entryDate = (value) => {
		if(!isValid(new Date(value))){
			return;
		}

		setEntryDate(dateToString(value));
	};
    //-----------------------------------------------
    // eventHandler - セルのクリック
    //-----------------------------------------------
	async function handleColumnClick(dock, e) {
		const rect = e.currentTarget.getBoundingClientRect();
		const y = e.clientY - rect.top;
		setKeepNewId(false);

		// ヘッダがスクロール領域内になったため、クリックYは「列内」だけの座標でOK（今の構造のままOK）
		const min = Math.max(0, Math.min(END_MINUTES - MINUTES_STEP, Math.floor(y / PX_PER_MIN + START_MINUTES)));
		const snapped = Math.round(min / MINUTES_STEP) * MINUTES_STEP;
		setForm((p) => ({ ...p, dock, senmeiCode: senmeiCode, start: snapped, end: (Math.min(snapped + 60 ,END_MINUTES)),outTime: false}))
		await openNew();
	}
	//-----------------------------------------------
    // eventHandler - セルのクリック
    //-----------------------------------------------
	async function handleClickOutTime() {
		setKeepNewId(true);

		setForm((p) => ({ ...p, senmeiCode: senmeiCode,outTime: true}))
		await openNew();
	}
	//-----------------------------------------------
    // eventHandler - 削除
    //-----------------------------------------------
	async function handleClickDelete () {
		await deleteData(editingId);
		handleCloseConfirm();
	}
	//-----------------------------------------------
    // eventHandler - 漁法チェックボックスのON/OFF
    //-----------------------------------------------
	function handleMethod (code, value) {
		if (value) {
			// チェックONとなった漁法を追加
			setForm({...form, method: [...form.method,code]});
		} else {
			// チェックOFFとなった漁法を削除
			setForm({...form, method: form.method.filter(item => item !== code)});
        }
    }
	//-----------------------------------------------
    // eventHandler - 荷主の変更
    //-----------------------------------------------
	async function handleChangeSenmei (e) {
		setForm((p) => ({ ...p, senmeiCode: e.target.value }));
		setKeepNewId(false);
		setEditingId("new");
		await searchDataSenmeiCodeAndDate(entryDate,e.target.value);
    }
    //-----------------------------------------------
    // eventHandler - 時刻マスのクリック（時刻選択ダイアログ）
    //-----------------------------------------------
    const handleSlotClick = (minutes) => {
        if (clickPrevIndex === minutes || startIndex !== endIndex){
            //開始時刻押下
			resetSelected();
			setStartIndex(minutes);
			setEndIndex(minutes);
			updateSelection(minutes, minutes);
        }else {
            //終了時刻押下
            setEndIndex(minutes);
            updateSelection(startIndex, minutes)
		}
        setClickPrevIndex(minutes);
	}
    //-----------------------------------------------
    // eventHandler - 編集データの変更
    //-----------------------------------------------
	async function handleChangeEditingId(value) {
		setKeepNewId(true);
        setEditingId(value);
		await searchDataSenmeiCodeAndDate(entryDate, form.senmeiCode);
    }
    //===============================================
    // Function
    //===============================================
    //-----------------------------------------------
    // Function - 新規入力
    //-----------------------------------------------
	async function openNew() {
		// 仲買人はスケジュール参照のみ可能
		if (authority == 3) {
			return;
		}
		await fetchData();

		setKeepNewId(false);
		setEditingId("new");
		await searchDataSenmeiCodeAndDate(entryDate,senmeiCode??getDataAll_ship_res?.Data[0]?.code);
		setOpen(true);
	}
    //-----------------------------------------------
    // Function - 明細表示
    //-----------------------------------------------
	async function openEdit(ev) {
		// 仲買人はスケジュール参照のみ可能
		if (authority == 3) {
			return;
		}
		await fetchData();
		await searchDataSenmeiCodeAndDate(entryDate, ev.senmeiCode);
		setEditingId(ev.scheduleId);
		setOpen(true);
	}
    //-----------------------------------------------
    // Function - 明細を閉じる
    //-----------------------------------------------
	function closeDialog() {
		setOpen(false);
	}
	//-----------------------------------------------
    // Function - データ取得
    //-----------------------------------------------
	async function fetchData() {
		getDataAll_ship();
		getDataAll_fishMethod();
		getDataMethodSenmeiList();
		searchDataByCalendar();
	}
    //-----------------------------------------------
    // Function - 登録前チェック
    //-----------------------------------------------
	async function saveDialog() {
		if (!(form.end > form.start)) return;
		if (form.start < START_MINUTES || form.end > END_MINUTES) return;

		const errorFields = [];
		if(!(1 <= form.dock && form.dock <= 5) || form.dock === undefined){
			errorFields.push("ドック");
		}
		if(!(getDataAll_ship_res?.Data ?? []).map(item => item.code).includes(form.senmeiCode)){
			errorFields.push("荷主");
		}
		if(errorFields.length > 0){
			setErrorMessage(`${errorFields.join(",")}の入力内容をご確認ください。`);
			handleClickSnackBarFalse();
			return;
		}

        if (editingId === "new") {
            await getNextId_func();
			return;
        }
        
        buildPayloadAndSetData(editingId,form.kaisu);
	}
    //-----------------------------------------------
    // Function - 登録処理
    //-----------------------------------------------
	function buildPayloadAndSetData(scheduleId,kaisu) {
        const methodList =
            Array.isArray(getDataAll_fishMethod_res?.Data)
                ? getDataAll_fishMethod_res.Data
                    .filter(item => form.method.includes(item.code))
                    .map(item => item.displayName)
                : [];
        const payload = {
            activeFlag: 1,
            scheduleId: scheduleId,
			scheduleDate: entryDate,
            lineNo: Number(form.dock),
            startMin: form.outTime ? outTimeStr : unformatHHMM(minutesToHHMM(form.start)),
            endMin: form.outTime ? outTimeStr : unformatHHMM(minutesToHHMM(form.end)),
            senmeiCode: form.senmeiCode,
            senmei: (getDataAll_ship_res?.Data ?? []).find((item) => item.code === form.senmeiCode)?.displayName ?? "",
            biko: (form.biko || "").trim(),
            methodCodeList: form.method,
            methodList: methodList,
			kaisu: kaisu
        };
        const submitData = async () => {
            await setData(payload);
            await searchDataByCalendar();
        }
        submitData();
    }
    //-----------------------------------------------
    // Function - 削除処理
    //-----------------------------------------------
	function deleteEvent() {
		if (!editingId) return;
		handleOpenConfirm();
	}
	//-----------------------------------------------
    // Function - 時刻選択を開く
    //-----------------------------------------------
    const openSelectTime = async () => {
		fetchData();
        setSelectTimeOpen(true);
		setStartIndex(form.start);
        setClickPrevIndex(form.start);
		setEndIndex(form.end - MINUTES_STEP);
		setSlotState((prev) => {
			return (
				prev.map((slot) => {
					const isReserved = (searchDataByCalendar_res?.Data ?? []).some((event) => {
						if (event.lineNo !== form.dock || event.scheduleId === editingId || isNaN(event.startMin) || isNaN(event.endMin)){
							return false;
						}
						const start = hhmmToMinutes(formatHHMM(event.scheduleDate, event.startMin));
						const end = hhmmToMinutes(formatHHMM(event.scheduleDate, event.endMin));
						return (
							slot.minutes >= start &&
							slot.minutes < end
						)
					})
					return {
						...slot,
						status: isReserved ? "×" : "○",
					}
				})
			)

		})
		updateSelection(form.start, form.end - MINUTES_STEP);
	}

    //-----------------------------------------------
    // Function - 時刻選択を閉じる
    //-----------------------------------------------
    const closeSelectTime = () => {
        setSelectTimeOpen(false);
    }

    //-----------------------------------------------
    // Function - 時刻を範囲選択する
    //-----------------------------------------------
    const updateSelection = (startMin, endMin) => {
		const start = Math.floor(startMin / MINUTES_STEP);
		const end = Math.floor(endMin / MINUTES_STEP);
		const step = start <= end ? 1 : -1;
        setSlotState((prev) => {
			const newSlots = prev.map(s => ({ ...s, selected: false}))
            if(step === 1) {
                for (let i = start; i <= end; i++) {
                    if(newSlots[i].status === "×"){
                        setEndIndex(newSlots[i - 1].minutes)
                        break;
                    }
                    newSlots[i].selected = true;
                }
            } else {
                for (let i = start; i >= end; i--) {
                    if(newSlots[i].status === "×"){
                        setEndIndex(newSlots[i + 1].minutes)
                        break;
                    }
                    newSlots[i].selected = true;
			    }
            }
			return newSlots;
		})
	}
    //-----------------------------------------------
    // Function - 時刻の範囲選択をリセット
    //-----------------------------------------------
	const resetSelected = () => {
		setSlotState(prev =>
			prev.map(s => ({
				...s,
				selected: false
			}))
		);
	};
    //-----------------------------------------------
    // Function - 時刻の範囲選択決定
    //-----------------------------------------------
	const saveSelectSlot = () => {
		const start = startIndex <= endIndex ? startIndex : endIndex;
		const end = (startIndex <= endIndex ? endIndex : startIndex) + MINUTES_STEP;
		setForm({...form, start, end})
		setSelectTimeOpen(false);
	}

	//===============================================
	// UI Message
	//===============================================
    // コンファームの表示
    const handleOpenConfirm = () => { setOpenConfirm(true) };
    
    // コンファームを閉じる
    const handleCloseConfirm = () => { setOpenConfirm(false) };

	// スナックバー(正常)を表示
    const handleClickSnackBarTrue = () => { setOpenSnackBarTrue(true) };
    
	// スナックバー(正常)を閉じる
    const handleCloseSnackBarTrue = (event, reason) => {
        if (reason === 'clickaway') { return } setOpenSnackBarTrue(false);
    };

	// スナックバー(異常)を表示
    const handleClickSnackBarFalse = () => { setOpenSnackBarFalse(true) };
    
	// スナックバー(異常)を閉じる
    const handleCloseSnackBarFalse = (event, reason) => {
        if (reason === 'clickaway') { 
			return; 
		}

		setOpenSnackBarFalse(false);
    };
	//===============================================
	// JSXのレンダー
	//===============================================
	const externalEvents = (getDataMethodSenmeiList_res?.Data ?? []).map(data => {
		const methodName =
			getDataAll_fishMethod_res?.Data?.find(item => item.code === data.methodCode)?.displayName
			?? data.method
			?? "";
		const senmeiText = (data.senmeiList ?? []).map(item => {
			return item.senmei
				|| getDataAll_ship_res?.Data?.find(ship => ship.code === item.senmeiCode)?.displayName
				|| "";
		}).filter(Boolean).join("、");
		return {
			id: data.methodCode,
			label: senmeiText ? `${methodName}【${senmeiText}】` : methodName,
		};
	});
	const senmeiMapping = Array.isArray(getDataAll_ship_res?.Data)
		? getDataAll_ship_res.Data.map((item) => ({
			id: item.id,
			name: item.displayName,
		}))
		: [];
	const outTimeSenmei = (searchDataByCalendar_res?.Data ?? []).filter(data => data.startMin === outTimeStr || data.endMin === outTimeStr);
	return (
		<>
			{systemMode!==1 &&(<Header loginInfo="on" backButton="on" backPage="/portHome" setEntryDate={handleChange_entryDate} />)}
			<div className='portSchedule bodyContens'>
				<TitleBar title="スケジュール" color="#006400" />
				<div className='mainContents'>
					<Frame classname="frame" title="出漁状況" color="#006400">
						<Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 3, rowGap: 0.5, alignContent: "flex-start", height: "70px", overflowY: "auto", ml: 2 }}>
							{externalEvents.map((method) => (
								<Typography key={method.id} variant="subtitle2" sx={{ fontWeight: 700, fontSize:'1,2em',textAlign: "left", maxWidth: "100%" }}>
									{method.label}
								</Typography>
							))}
						</Box>
					</Frame>
					<Frame classname="frame" title="入船予定" color="#006400">
						<Box sx={{ p: 0 }}>
							<Card variant="outlined" sx={{ overflow: "hidden" }}>
								<Box
									ref={scrollRef}
									sx={{
										maxHeight: systemMode !== 1 ? "53vh" : "59vh",
										overflowY: "auto",
										bgcolor: "background.default",
										// 対応ブラウザではスクロールバー領域を常に確保してズレを抑える
										scrollbarGutter: "stable",
									}}
									>
								{/* ヘッダ（sticky） */}
								<Box
									sx={{
										height: HEADER_H,
										display: "flex",
										alignItems: "center",
										borderBottom: "1px solid",
										borderColor: "#fff",
										bgcolor: "#222B35",
										color:"#fff",
										position: "sticky",
										top: 0,
										zIndex: 10,
									}}
								>
									<Box sx={{ width: TIME_COL_W, px: 1 }}>
										<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
											時刻
										</Typography>
									</Box>

									<Box sx={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(5, 1fr)" }}>
										{DOCKS.map((d) => (
											<Box
											key={d}
											sx={{
												px: 1,
												borderLeft: "1px solid",
												borderColor: "divider",
											}}
											>
											<Typography variant="subtitle2" sx={{ fontWeight: 700, textAlign: "center" }}>
												{d}番ドック
											</Typography>
											</Box>
										))}
									</Box>
								</Box>

								{/* ボディ本体 */}
								<Box sx={{ display: "flex" }}>
									{/* 時刻列 */}
									<Box sx={{ width: TIME_COL_W, position: "relative", height: bodyH }}>
									{timeSlot.map((m) => {
										const showLabel = m.minutes % 60 === 0;
										if(m.minutes >= OPERATING_MINUTES){
											return null;
										}
										return (
										<Box
											key={m.minutes}
											sx={{
												position: "absolute",
												top: m.minutes * PX_PER_MIN,
												left: 0,
												right: 0,
												height: MINUTES_STEP * PX_PER_MIN,
												borderTop: "1px solid",
												borderColor: "divider",
												px: 1,
												display: "flex",
												alignItems: "flex-start",
												justifyContent: "flex-end",
											}}
										>
											{showLabel && m.minutes !== OPERATING_MINUTES && (
											<Typography variant="caption" sx={{ opacity: 0.75 }}>
												{minutesToHHMM(m.minutes + (START_MINUTES))}
											</Typography>
											)}
										</Box>
										);
									})}
									</Box>

									{/* ドック列 */}
									<Box sx={{ flex: 1, position: "relative", height: bodyH }}>
										{/* 現在時刻ライン（スクロール領域内なので sticky ヘッダに隠れないよう zIndex調整済み） */}
										{nowY >= START_MINUTES * PX_PER_MIN && nowY <= END_MINUTES * PX_PER_MIN && (
											<Box
												sx={{
													position: "absolute",
													left: 0,
													right: 0,
													top: nowY - START_MINUTES * PX_PER_MIN,
													height: 0,
													borderTop: "2px solid",
													borderColor: "error.main",
													opacity: 0.65,
													zIndex: 3,
													pointerEvents: "none",
												}}
											/>
											)}
										<Box
											sx={{
												position: "absolute",
												inset: 0,
												display: "grid",
												gridTemplateColumns: "repeat(5, 1fr)",
												zIndex: 1,
											}}
										>
											{DOCKS.map((dock) => (
											<Box
												key={dock}
												onClick={async (e) => await handleColumnClick(dock, e)}
												sx={{
												position: "relative",
												borderLeft: "1px solid",
												borderColor: "divider",
												cursor: "pointer",
												backgroundImage: `repeating-linear-gradient(
													to bottom,
													transparent 0px,
													transparent ${(MINUTES_STEP * PX_PER_MIN) - 1}px,
													rgba(0,0,0,0.08) ${(MINUTES_STEP * PX_PER_MIN) - 1}px,
													rgba(0,0,0,0.08) ${(MINUTES_STEP * PX_PER_MIN)}px
												)`,
												}}
											/>
											))}
										</Box>

										{/* 予定ブロック */}
										{(searchDataByCalendar_res?.Data ?? []).map((ev) => {
											const colIndex = DOCKS.indexOf(ev.lineNo);
											if (colIndex < 0) return null;
											if (isNaN(ev.startMin) || isNaN(ev.endMin)) return null;

											const leftPct = (colIndex * 100) / DOCKS.length;
											const widthPct = 100 / DOCKS.length;
											const startMin = hhmmToMinutes(formatHHMM(ev.scheduleDate,ev.startMin));
											const endMin = hhmmToMinutes(formatHHMM(ev.scheduleDate,ev.endMin));
											const top = (startMin - START_MINUTES) * PX_PER_MIN;
											const height = Math.max(18, (endMin - startMin) * PX_PER_MIN);

											return (
											<Box
												key={ev.id}
												onClick={async (e) => {
													e.stopPropagation();
													await openEdit(ev);
												}}
												sx={{
													position: "absolute",
													left: `calc(${leftPct}% + 6px)`,
													width: `calc(${widthPct}% - 12px)`,
													top,
													height,
													borderRadius: 1.5,
													border: "1px solid",
													borderColor: "divider",
													bgcolor: "#4682b4",
													color:"#fff",
													boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
													p: 1,
													overflow: "hidden",
													cursor: "pointer",
													zIndex: 2,
												}}
											>
												<Typography variant="caption" sx={{ fontWeight: 800, display: "block" }}>
													{ev.senmei}
												</Typography>
												<Typography variant="caption" sx={{ opacity: 0.85 }}>
													{minutesToHHMM(startMin)}–{minutesToHHMM(endMin)}
												</Typography>
											</Box>
											);
										})}
										</Box>
									</Box>
								</Box>
							</Card>

							{/* 追加/編集ダイアログ */}
							<Dialog open={open} onClose={closeDialog} maxWidth="sm">
								<DialogTitle>
									<TitleBar title="スケジュール登録" color="#006400" />
								</DialogTitle>
								<DialogContent style={{border:'none'}} >
									<Stack spacing={2} sx={{ mt: 1 }}>
										<TextField
											label="ドック（1〜5）"
											type="number"
											sx={{width:'380px'}}
											inputProps={{ min: 1, max: 5 }}
											value={form.dock}
											onChange={(e) => {
												const dock = e.target.value === '' ? undefined : Number(e.target.value)
												setForm((p) => ({ ...p, dock }))
											}}
										/>
										<Grid container gap={2} alignItems={"center"}>
											<Button variant="outlined" sx={{ width:'120px' }} onClick={()=>openSelectTime()} disabled={form.outTime}>
												時刻選択
											</Button>
											<Typography sx={{ alignContent: 'center', visibility: form.outTime ? 'hidden' : 'visible'}}>
												{minutesToHHMM(form.start)} - {minutesToHHMM(form.end)}
											</Typography>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
												<Switch
													checked={form.outTime}
													onChange={(_, checked) => {
														setForm((p) => ({ ...p, outTime: checked }));
													}}
												/>
												<Typography>時間外</Typography>
											</Box>
										</Grid>

                                        <CustomSelect
                                            id         = "schedule_senmei"
                                            label      = "荷主"
                                            width      = "380px"
                                            dataList   = {senmeiMapping ?? []} 
                                            selectedId = {form.senmeiCode} 
                                            onChange   = {(e) => handleChangeSenmei(e)}
                                        />
										<TextField
											select
											label="回数"
											value={editingId}
											sx={{ 
												width: '380px', '& .MuiInputBase-root': {
													height: 42,
												}, 
											}}
											onChange={(e) => handleChangeEditingId(e.target.value)}
										>
											{(sortedSchedule ?? []).map((item, index) => 
											(
												<MenuItem key={item.scheduleId} value={item.scheduleId}>{`${item.kaisu}回目`}</MenuItem>
											))}
											<MenuItem key={"new"} value={"new"}>{maxKaisu + 1 + "回目"}</MenuItem>
										</TextField>

										<div style={{backgroundColor:'#fff',border : '1px solid #ababab',borderRadius : '10px', padding : '10px', height:"350px", width:"380px", overflowY:'scroll'}}>
										{
											(getDataAll_fishMethod_res?.Data ?? []).map(item => (
												<CustomCheckbox
													key={item.code}
													defaultChecked = { form.method.includes(item.code) }
													onChange = { (e) => handleMethod(item.code,e.target.checked) }
													label = {item.displayName}
													style= {{width:'200px', textAlign:'left', fontSize:'4.5rem'}}
												/>
											))
										}
										</div>

										<TextField
											label="備考"
											sx={{width:'380px'}}
											value={form.biko}
											onChange={(e) => setForm((p) => ({ ...p, biko: e.target.value }))}
											multiline
											minRows={3}
										/>
									</Stack>
								</DialogContent>

								<DialogActions>
								{editingId!=="new" && (
									<Button color="error" onClick={deleteEvent}>
									削除
									</Button>
								)}
								<Box sx={{ flex: 1 }} />
								<Button onClick={closeDialog}>キャンセル</Button>
								<Button
									variant="contained"
									onClick={saveDialog}
									style = {{ margin: '1rem', color:'#fff', backgroundColor: '#006400' }}
								>
									保存
								</Button>
								</DialogActions>
							</Dialog>
							<Dialog disableRestoreFocus fullWidth open={selectTimeOpen} onClose={() => closeSelectTime()}>
								<DialogTitle>
									<TitleBar title="時刻選択" color="#006400" />
								</DialogTitle>
								<DialogContent>
									{(groupedByHour ?? []).map((hourRow, hour) => (
										<Grid key={hourRow[0].minutes} container gap={1} sx={{padding: 0.5, alignItems: 'center', justifyContent: 'center'}}>
											<Grid container key={hour} sx={{ width: 40, justifyContent: 'right' }} >
												<Typography key={hour}>{hour + START_HOURS}時</Typography>
											</Grid>
												{hourRow.map((minRow) => (
												<ButtonBase 
													key={minRow.minutes}
													onClick={() => handleSlotClick(minRow.minutes)}
													sx={{ 
														width: '18%',
														height: 64,
														border: '1px solid #ccc',
														borderRadius: 1,
														bgcolor: minRow.selected ? '#006400' : 'transparent',
														color: minRow.selected ? '#fff' : 'text.primary',
														'&.Mui-disabled': {
															opacity: 0.4,
															cursor: 'not-allowed',
														},
													}}
													disabled={minRow.status === "×"}
												>
													<Box sx={{
														height: '100%',
														width: '100%',
														display: 'flex',
														flexDirection: 'column',
														alignItems: 'center',
														justifyContent: 'center',
													}}>
														<Typography key={minRow.minutes}>
																{minutesToHHMM(minRow.minutes)}
														</Typography>
														<Typography  variant="h6">
															{minRow.status}
														</Typography>
													</Box>
												</ButtonBase>
												))}
										</Grid>
									))}
								</DialogContent>
								<DialogActions>
									<Button onClick={closeSelectTime}>キャンセル</Button>
									<Button
										onClick={saveSelectSlot}
										style = {{ margin: '1rem', color:'#fff', backgroundColor: '#006400' }}
									>
										決定
									</Button>
								</DialogActions>
							</Dialog>
						</Box>
					</Frame>
					<Frame classname="frame" title={`時間外荷主一覧${outTimeSenmei.length !==0 ? `：${outTimeSenmei.length}隻` : ""}`} color="#006400">
						<Box sx={{ display: "flex", flexWrap: "nowrap", gap: 2, minHeight: "4vh", m: 1, alignItems: "center", overflowX:'auto'}}>
							<Button
								color   = "primary"
								sx={{
									color: '#fff',
									whiteSpace: 'nowrap',
									minWidth: 'fit-content',
									paddingTop: "4px",
									paddingBottom: "4	px",
								}}
								size    = "midium"
								variant = "contained"
								startIcon={<AddCircleIcon />}
								onClick={async (e) => await handleClickOutTime()}
							>
								新　規
							</Button>
							{outTimeSenmei.map((ev) => {
								return (
									<Chip
										key={ev.scheduleId}
										label={`${ev.senmei}(${ev.lineNo ? `${ev.lineNo}番ドック` : ""}${ev.kaisu}回目)`}
										clickable
										variant="subtitle2"
										sx={{ textAlign: "left", bgcolor: "#4682b4", color: "#fff", }}
										onClick={async (e) => {
											e.stopPropagation();
											await openEdit(ev);
										}}
									/>
								);
							})}
						</Box>
					</Frame>
				</div>
			</div>
			<CustomSnackBar
				open            = {openSnackBarTrue}
				onClose         = {() => handleCloseSnackBarTrue()}
				severity        = "success"
				backgroundColor = "#2e8b57"
				message         = "登録しました"
			/>
			<CustomSnackBar
				open            = {openSnackBarFalse}
				onClose         = {() => handleCloseSnackBarFalse()}
				severity        = "error"
				backgroundColor = "#db7093"
				message         = {errorMessage}
            />
			<CustomConfirm
				open      = {openConfirm}
				onClose   = {() => handleCloseConfirm()}
				onClickNG = {() => handleCloseConfirm()}
				onClickOK = {async () => await handleClickDelete()}
				message   = "指定データの削除を行います。よろしいですか？"
			/>
		</>
	);
};

export default PortSchedule;
